import React, { useState } from 'react';
import { Edit, Save, FileCode, Plus } from 'lucide-react';
import { ITEM_TYPE_LABELS } from './surveyItemTypes';

/**
 * Detail view component for editing a survey item
 * 
 * @param {Object} props - Component props
 * @param {Object} props.selectedItem - The currently selected survey item
 * @param {Function} props.updateItem - Function to update the item
 */
const SurveyItemDetail = ({ selectedItem, updateItem }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isRawMode, setIsRawMode] = useState(false);
  const [editedItem, setEditedItem] = useState(null);
  
  if (!selectedItem) {
    return <div className="p-4 text-center text-gray-500">Select an item to edit</div>;
  }
  
  const handleEdit = () => {
    setEditedItem({ ...selectedItem });
    setIsEditing(true);
  };
  
  const handleSave = () => {
    updateItem(editedItem);
    setIsEditing(false);
    setEditedItem(null);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditedItem(null);
  };
  
  const handleAddOption = () => {
    setEditedItem({
      ...editedItem,
      options: [...editedItem.options, {
        text: '',
        isVisible: true,
        isTerminate: false,
        isRotate: false,
        isOpen: false
      }]
    });
  };
  
  const handleRemoveOption = (index) => {
    const newOptions = [...editedItem.options];
    newOptions.splice(index, 1);
    setEditedItem({
      ...editedItem,
      options: newOptions
    });
  };
  
  // Not using handleOptionChange directly as we're using inline methods in the UI

  // Get question number if present
  const questionNumberMatch = selectedItem.content?.match(/^Q\.(\d+)\s/);
  const questionNumber = questionNumberMatch ? questionNumberMatch[1] : null;

  return (
    <div className="bg-white rounded-lg shadow p-2 h-full overflow-auto relative">
      <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
        <div className="flex flex-col max-w-[calc(100%-120px)]">
          <h2 className="text-xl font-semibold text-blue-800 flex items-center truncate">
            {questionNumber && (
              <span className="inline-flex items-center justify-center bg-blue-600 text-white rounded-full w-7 h-7 mr-2 text-[12px] border-2 border-white shadow-md transition-all duration-200 flex-shrink-0">
                {questionNumber}
              </span>
            )}
            <span className="truncate">{selectedItem.refId}</span>
          </h2>
          {selectedItem.title && (
            <div className="text-lg text-gray-600 mt-1 truncate">{selectedItem.title}</div>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0 ml-2">
          <button 
            className={`px-2 py-1 rounded text-[11px] whitespace-nowrap ${isRawMode ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'} hover:bg-blue-200`}
            onClick={() => setIsRawMode(!isRawMode)}
          >
            <FileCode size={11} className="inline mr-1" />
            {isRawMode ? "Normal" : "Raw"}
          </button>
          {!isEditing ? (
            <button 
              className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-[11px] whitespace-nowrap"
              onClick={handleEdit}
            >
              <Edit size={11} className="inline mr-1" />
              Edit
            </button>
          ) : (
            <>
              <button 
                className="px-2 py-1 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-[11px] whitespace-nowrap"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button 
                className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-[11px] whitespace-nowrap"
                onClick={handleSave}
              >
                <Save size={11} className="inline mr-1" />
                Save
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Item metadata */}
      <div className="grid grid-cols-4 gap-3 p-3 mb-4 bg-gray-50 rounded-lg text-[13px]">
        <div>
          <label className="block text-[12px] text-gray-500 mb-1">Title</label>
          {isEditing ? (
            <input 
              type="text" 
              className="w-full p-1.5 border rounded text-[13px]"
              value={editedItem.title}
              onChange={(e) => setEditedItem({...editedItem, title: e.target.value})}
            />
          ) : (
            <div className="font-medium">{selectedItem.title}</div>
          )}
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1">Type</label>
          {isEditing ? (
            <select 
              className="w-full p-1.5 border rounded text-[13px]"
              value={editedItem.type}
              onChange={(e) => setEditedItem({...editedItem, type: e.target.value})}
            >
              {Object.entries(ITEM_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          ) : (
            <div className="capitalize font-medium">{ITEM_TYPE_LABELS[selectedItem.type] || selectedItem.type}</div>
          )}
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1">Visibility</label>
          {isEditing ? (
            <div className="flex items-center space-x-2 text-[12px]">
              <div>
                <input 
                  type="checkbox" 
                  id="delete"
                  className="mr-1 h-4 w-4"
                  checked={editedItem.delete}
                  onChange={(e) => setEditedItem({...editedItem, delete: e.target.checked})}
                />
                <label htmlFor="delete">Delete</label>
              </div>
              <div>
                <input 
                  type="checkbox" 
                  id="noPrint"
                  className="mr-1 h-4 w-4"
                  checked={editedItem.noPrint}
                  onChange={(e) => setEditedItem({...editedItem, noPrint: e.target.checked})}
                />
                <label htmlFor="noPrint">No Print</label>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 font-medium">
              {selectedItem.delete && <span className="text-red-600 text-[12px]">Delete</span>}
              {selectedItem.noPrint && <span className="text-orange-600 text-[12px]">No Print</span>}
              {!selectedItem.delete && !selectedItem.noPrint && <span className="text-green-600 text-[12px]">Visible</span>}
            </div>
          )}
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1">Options Style</label>
          {isEditing ? (
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="numberedOptions"
                className="mr-1 h-4 w-4"
                checked={editedItem.isNumberedOptions}
                onChange={(e) => setEditedItem({...editedItem, isNumberedOptions: e.target.checked})}
              />
              <label htmlFor="numberedOptions" className="text-[12px]">Numbered Options</label>
            </div>
          ) : (
            <div className="text-[13px] font-medium">
              {selectedItem.isNumberedOptions ? 'Numbered Options' : 'Checkbox Options'}
              {((selectedItem.delete && selectedItem.noPrint) || (!selectedItem.delete && selectedItem.noPrint) || (selectedItem.delete && !selectedItem.noPrint)) ? '(Hidden)' : ''}
            </div>
          )}
        </div>
      </div>
      
      {isRawMode ? (
        <div className="font-mono text-[13px] p-3 bg-gray-900 text-gray-100 rounded-lg overflow-auto whitespace-pre leading-relaxed">
          {JSON.stringify(selectedItem, null, 2)}
        </div>
      ) : (
        <>
          {/* Question Content */}
          <div className="mb-5">
            <label className="block text-[14px] font-semibold text-gray-700 mb-2">Question Text</label>
            {isEditing ? (
              <textarea
                className="w-full p-3 border rounded min-h-[130px] text-[14px]"
                value={editedItem.content}
                onChange={(e) => setEditedItem({...editedItem, content: e.target.value})}
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded whitespace-pre-wrap text-[14px] text-left leading-relaxed">{selectedItem.content}</div>
            )}
          </div>
          
          {/* Options */}
          {((selectedItem.options && selectedItem.options.length > 0) || isEditing) && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-[14px] font-semibold text-gray-700">Response Options</label>
                {isEditing && (
                  <button 
                    className="text-blue-600 hover:text-blue-800 flex items-center text-[13px]"
                    onClick={handleAddOption}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Option
                  </button>
                )}
              </div>
              
              <ul className="bg-gray-50 rounded-lg text-left">
                {isEditing ? (
                  editedItem.options.map((option, index) => (
                    <li key={index} className="p-3 border-b last:border-b-0 flex items-center">
                      <span className="w-7 h-7 flex items-center justify-center mr-3 bg-blue-100 text-blue-700 rounded-full text-[13px]">
                        {index + 1}
                      </span>
                      <input
                        type="text"
                        className="flex-1 p-2 border rounded text-[13px]"
                        value={option.text}
                        onChange={(e) => {
                          const newOptions = [...editedItem.options];
                          newOptions[index] = {...newOptions[index], text: e.target.value};
                          setEditedItem({...editedItem, options: newOptions});
                        }}
                      />
                      <div className="flex ml-3 space-x-3">
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id={`visible-${index}`}
                            className="h-4 w-4"
                            checked={option.isVisible}
                            onChange={(e) => {
                              const newOptions = [...editedItem.options];
                              newOptions[index] = {...newOptions[index], isVisible: e.target.checked};
                              setEditedItem({...editedItem, options: newOptions});
                            }}
                          />
                          <label htmlFor={`visible-${index}`} className="ml-1 text-[11px]">Visible</label>
                        </div>
                        <div className="flex items-center">
                          <input 
                            type="checkbox" 
                            id={`terminate-${index}`}
                            className="h-4 w-4"
                            checked={option.isTerminate}
                            onChange={(e) => {
                              const newOptions = [...editedItem.options];
                              newOptions[index] = {...newOptions[index], isTerminate: e.target.checked};
                              setEditedItem({...editedItem, options: newOptions});
                            }}
                          />
                          <label htmlFor={`terminate-${index}`} className="ml-1 text-[11px]">Term</label>
                        </div>
                        <button 
                          className="ml-2 text-red-500 hover:text-red-700 text-base"
                          onClick={() => handleRemoveOption(index)}
                        >
                          &times;
                        </button>
                      </div>
                    </li>
                  ))
                ) : (
                  selectedItem.options.map((option, index) => (
                    <li key={index} className="p-3 border-b last:border-b-0 flex items-center">
                      <span className="w-7 h-7 flex items-center justify-center mr-3 bg-blue-100 text-blue-700 rounded-full text-[13px]">
                        {index + 1}
                      </span>
                      <span className={`text-[14px] ${option.isVisible ? '' : 'opacity-50'}`}>
                        {option.text}
                        {option.isTerminate && <span className="ml-1 text-red-600 font-bold text-[13px]">(TERM)</span>}
                        {option.isOpen && <span className="ml-1 text-blue-600 font-italic text-[13px]">(Open)</span>}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Concerns section or similar - shown in left-aligned format */}
      {selectedItem.concerns && (
        <div className="mt-5">
          <label className="block text-[14px] font-semibold text-gray-700 mb-2">Concerns</label>
          <div className="p-3 bg-gray-50 rounded text-[14px] text-left leading-relaxed">
            {selectedItem.concerns}
          </div>
        </div>
      )}

      {/* Options list to show 1. XX format */}
      {selectedItem.displayOptions && selectedItem.displayOptions.length > 0 && (
        <div className="mt-5">
          <label className="block text-[14px] font-medium text-gray-700 mb-2">Options:</label>
          <div className="p-3 bg-gray-50 rounded text-[14px] text-left">
            <ol className="list-decimal list-inside space-y-2">
              {selectedItem.displayOptions.map((option, index) => (
                <li key={index} className="mb-2">{option}</li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyItemDetail;